import { useMemo } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';

import type { CreateWebsiteCategoryPageRequest } from '@utazzo/shared';

import { OrderedPickerList } from '@/components/ordered-picker/OrderedPickerList';
import { usePackagesList } from '@/features/packages/use-packages';

export const BestSellersEditor = ({
  form,
  disabled,
}: {
  form: UseFormReturn<CreateWebsiteCategoryPageRequest>;
  disabled?: boolean;
}): React.JSX.Element => {
  const bestSellers = useWatch({ control: form.control, name: 'bestSellers' }) ?? [];

  const packagesQuery = usePackagesList({
    page: 1,
    pageSize: 200,
    sortBy: 'updatedAt',
    sortDir: 'desc',
    status: 'ACTIVE',
  });

  const options = useMemo(
    () =>
      (packagesQuery.data?.data ?? []).map((p) => ({
        id: p.id,
        label: p.name,
        description: p.slug,
      })),
    [packagesQuery.data],
  );

  const selectedIds = useMemo(
    () => bestSellers.map((b) => b.packageId).filter(Boolean),
    [bestSellers],
  );

  const syncBestSellers = (ids: string[]): void => {
    form.setValue(
      'bestSellers',
      ids.map((packageId) => ({ packageId })),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">
        Featured packages in the &quot;Best Sellers&quot; carousel at the top of the page.
      </p>
      <OrderedPickerList
        options={options}
        value={selectedIds}
        onChange={syncBestSellers}
        loading={packagesQuery.isLoading}
        disabled={disabled}
        maxSelected={12}
        pickerLabel="Packages"
        pickerHint="Select packages to feature, then drag to set carousel order."
        searchPlaceholder="Search packages…"
        sortLabel="Carousel order"
        sortEmptyMessage="No packages selected. Pick packages from the list above."
      />
    </div>
  );
};
